// AUTO-GENERATED from services/app-api-service/codeigniter/application/contracts/Subscriptions_Billing360Response.php
// Do not edit by hand — edit the PHP ResponseContract and the sync hook will regenerate this file.
// Bulk regeneration: scripts/sync-contracts/sync-all.sh

/**
 * Subscriptions_Billing360Response — per-franchise billing composite.
 *
 * Returned by GET /subscriptions/admin/billing-360/{franquia_id}.
 * Full-gated, on-demand single-franchise load (no N+1 over all franchises).
 *
 * Envelope keys:
 *   subscription   — live subscription summary (null when no subscription)
 *   grace_windows  — active grace_periods rows (includes revoked for full history)
 *   credits        — wallet balance summary (null when no wallet row exists)
 *   ledger         — last 20 ai_credit_ledger rows, newest first
 *   transactions   — platform_revenue_transactions rows, newest first
 *   events         — subscription_events timeline, newest first
 */
import type { Subscriptions_Billing360CreditsRow } from './Subscriptions_Billing360CreditsRow'
import type { Subscriptions_Billing360GraceRow } from './Subscriptions_Billing360GraceRow'
import type { Subscriptions_Billing360LedgerRow } from './Subscriptions_Billing360LedgerRow'
import type { Subscriptions_Billing360SubscriptionRow } from './Subscriptions_Billing360SubscriptionRow'
import type { Subscriptions_EventRow } from './Subscriptions_EventRow'
import type { Subscriptions_TransactionRow } from './Subscriptions_TransactionRow'
import type { ApiResponse } from '../services/httpService'
import { registerResponseContract } from './validate'

export interface Subscriptions_Billing360ResponseDados {
subscription: Subscriptions_Billing360SubscriptionRow
subscriptions: Subscriptions_Billing360SubscriptionRow[]
grace_windows: Subscriptions_Billing360GraceRow[]
credits: Subscriptions_Billing360CreditsRow
ledger: Subscriptions_Billing360LedgerRow[]
transactions: Subscriptions_TransactionRow[]
events: Subscriptions_EventRow[]
}

export type Subscriptions_Billing360Response = ApiResponse<Subscriptions_Billing360ResponseDados>;

registerResponseContract('Subscriptions_Billing360Response', {
  subscription: {
    type: 'object',
    required: false,
    fields: {
      subscription_id:    { type: 'number',  required: true },
      effective_status:   { type: 'string',  required: false },
      raw_status:         { type: 'string',  required: false },
      plan_id:            { type: 'number',  required: false },
      plan_name:          { type: 'string',  required: false },
      currency:           { type: 'string',  required: false },
      price_cents:        { type: 'number',  required: false },
      provider:           { type: 'string',  required: false },
      paid_through:       { type: 'string',  required: false },
      grace_until:        { type: 'string',  required: false },
      current_period_end: { type: 'string',  required: false },
      created_at:         { type: 'string',  required: false },
      can_reconcile:      { type: 'boolean', required: false },
    },
  },
  subscriptions: {
    type: 'array',
    required: true,
    items: {
      subscription_id:    { type: 'number',  required: true },
      effective_status:   { type: 'string',  required: false },
      raw_status:         { type: 'string',  required: false },
      plan_id:            { type: 'number',  required: false },
      plan_name:          { type: 'string',  required: false },
      currency:           { type: 'string',  required: false },
      price_cents:        { type: 'number',  required: false },
      provider:           { type: 'string',  required: false },
      paid_through:       { type: 'string',  required: false },
      grace_until:        { type: 'string',  required: false },
      current_period_end: { type: 'string',  required: false },
      created_at:         { type: 'string',  required: false },
      can_reconcile:      { type: 'boolean', required: false },
    },
  },
  grace_windows: {
    type: 'array',
    required: true,
    items: {
      id:              { type: 'number',  required: true },
      subscription_id: { type: 'number',  required: false },
      plan_id:         { type: 'number',  required: false },
      starts_at:       { type: 'string',  required: false },
      ends_at:         { type: 'string',  required: false },
      source:          { type: 'string',  required: false },
      granted_by:      { type: 'string',  required: false },
      reason:          { type: 'string',  required: false },
      revoked_at:      { type: 'string',  required: false },
    },
  },
  credits: {
    type: 'object',
    required: false,
    fields: {
      balance_micro:  { type: 'number',  required: false },
      reserved_micro: { type: 'number',  required: false },
    },
  },
  ledger: {
    type: 'array',
    required: true,
    items: {
      id:           { type: 'number',  required: true },
      entry_type:   { type: 'string',  required: false },
      signed_micro: { type: 'number',  required: false },
      request_id:   { type: 'string',  required: false },
      created_at:   { type: 'string',  required: false },
    },
  },
  transactions: {
    type: 'array',
    required: true,
    items: {
      id:                 { type: 'number',  required: true },
      subscription_id:    { type: 'number',  required: true },
      kind:               { type: 'string',  required: false },
      amount_cents:       { type: 'number',  required: false },
      currency:           { type: 'string',  required: false },
      status:             { type: 'string',  required: false },
      provider:           { type: 'string',  required: false },
      provider_charge_id: { type: 'string',  required: false },
      paid_at:            { type: 'string',  required: false },
      created_at:         { type: 'string',  required: false },
      attempts: {
        type: 'array',
        required: false,
        items: {
          status:     { type: 'string',  required: false },
          event_type: { type: 'string',  required: false },
          created_at: { type: 'string',  required: false },
        },
      },
    },
  },
  events: {
    type: 'array',
    required: true,
    items: {
      id:              { type: 'number',  required: true },
      provider:        { type: 'string',  required: false },
      subscription_id: { type: 'number',  required: false },
      event_type:      { type: 'string',  required: false },
      event_id:        { type: 'string',  required: false },
      status:          { type: 'string',  required: false },
      payload:         { type: 'string',  required: false },
      error:           { type: 'string',  required: false },
      processed_at:    { type: 'string',  required: false },
      created_at:      { type: 'string',  required: false },
    },
  },
})
